import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import {
  fetchSapAuthRequestById,
  fetchSapAuthMasterAll,
  approveRejectSapAuthRequest,
  ISapAuthorizationRequest,
  ISapAuthorizationTCode,
  ISapMasterOption,
} from '../../../core/data/redux/sapAuthorizationSlice';
import { getMasterText, getSapStatusBadge } from './SapAuthorizationList';

const SapAuthorizationApproval: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const sapState = useSelector((state: RootState) => state.sapAuthorizationState);
  const [remarks, setRemarks] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const loadDetails = () => {
    if (id) {
      dispatch(fetchSapAuthRequestById({ ReferenceNumber: id }));
    }
  };

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchSapAuthMasterAll({}));
    loadDetails();
  }, [dispatch, id]);

  const masterData = sapState.masterData || {};
  const plantsList: ISapMasterOption[] = masterData.Plants || [];
  const wingsList: ISapMasterOption[] = masterData.Wings || [];
  const businessAreasList: ISapMasterOption[] = masterData.Business_Areas || [];
  const purchaseOrgsList: ISapMasterOption[] = masterData.Purchase_Organizations || [];
  const authRevokedList: ISapMasterOption[] = masterData.Authorization_Revoked || [];
  const serversList: ISapMasterOption[] = masterData.SAP_servers || [];
  const modulesList: ISapMasterOption[] = masterData.SAP_Modules || [];

  const rawDetail = sapState.detailData;
  const reqData: ISapAuthorizationRequest = rawDetail && typeof rawDetail === 'object' ? rawDetail : {};

  const rawTcodes: any = reqData?.ToTcodes;
  const tcodesList: ISapAuthorizationTCode[] =
    rawTcodes && typeof rawTcodes === 'object' && Array.isArray(rawTcodes.results)
      ? rawTcodes.results
      : Array.isArray(rawTcodes)
        ? rawTcodes
        : Array.isArray((reqData as any)?.results)
          ? (reqData as any).results
          : [];

  const rawRemarks: any = reqData?.ToRemarks;
  const remarksTrail: any[] =
    rawRemarks && typeof rawRemarks === 'object' && Array.isArray(rawRemarks.results)
      ? rawRemarks.results
      : Array.isArray(rawRemarks)
        ? rawRemarks
        : [];

  const lastApproverEntry = [...remarksTrail]
    .reverse()
    .find((r: any) => r.ApproverName && String(r.ApproverName).trim() && r.Usertype === 'A') ||
    [...remarksTrail].reverse().find((r: any) => r.ApproverName && String(r.ApproverName).trim() && String(r.ApproverUserID).trim() !== String(reqData?.EmpID || '').trim());

  const displayApproverName =
    (reqData.approverName && String(reqData.approverName).trim()) ||
    (reqData.ApproverName && String(reqData.ApproverName).trim()) ||
    (reqData.LatestApproverName && String(reqData.LatestApproverName).trim()) ||
    (lastApproverEntry?.ApproverName && String(lastApproverEntry.ApproverName).trim()) ||
    '-';

  const displayApproverId =
    (reqData.ApproverUserID && String(reqData.ApproverUserID).trim()) ||
    (reqData.ApproverID && String(reqData.ApproverID).trim()) ||
    (lastApproverEntry?.ApproverUserID && String(lastApproverEntry.ApproverUserID).trim()) ||
    '-';

  const plantName = getMasterText(reqData.Plant, plantsList);
  const businessAreaName = getMasterText(reqData.BusinessArea, businessAreasList);
  const purchaseOrgName = getMasterText(reqData.PurchasingOrganization, purchaseOrgsList);
  const authRevokedName = getMasterText(reqData.AuthorizationRevoke, authRevokedList);
  const serverName = getMasterText(reqData.SAPServer, serversList);
  const moduleName = getMasterText(reqData.SAPModule, modulesList);
  const wingName = getMasterText(reqData.Wing, wingsList);

  const formatDate = (val?: string) => {
    if (!val) return '-';
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    } catch { }
    return val;
  };

  const employeesState = useSelector((state: RootState) => state.employeesState);
  const profile = employeesState?.data?.data?.details || employeesState?.data?.details;
  const loggedInEmpId = String(profile?.empId || profile?.employee_id || '');

  const handleAction = async (action: 'A' | 'R') => {
    if (!id) return;
    if (action === 'R' && !remarks.trim()) {
      toast.warning('Rejection remarks are mandatory. Please provide a reason.');
      return;
    }
    setIsProcessing(true);
    try {
      const refNo = String(reqData?.ReferenceNumber || reqData?.referenceNumber || id || '');
      const empId = String(reqData?.EmpID || reqData?.empId || '');
      const approverEmpId = loggedInEmpId || String(reqData?.ApproverUserID || reqData?.ApproverID || '00022088');

      const res = await dispatch(
        approveRejectSapAuthRequest({
          Action: action,
          ReferenceNumber: refNo,
          EmpID: empId,
          ApproverID: approverEmpId,
          ApproverUserID: approverEmpId,
          ARemarks: remarks.trim(),
          Remarks: remarks.trim(),
        })
      );

      if (res.meta.requestStatus === 'fulfilled') {
        toast.success(
          action === 'A'
            ? 'SAP Authorization request approved successfully!'
            : 'SAP Authorization request rejected successfully.'
        );
        navigate(routes.todoWorklist?.tasks || '/todo-worklist/tasks');
      } else {
        toast.error((res.payload as string) || 'Failed to process approval request');
      }
    } catch {
      toast.error('An unexpected error occurred while processing approval action');
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = sapState.loading === 'pending' && !rawDetail;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Bar */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center gap-2 mb-1">
              <h3 className="page-title mb-0 text-dark fw-bold">
                <i className="ti ti-lock-access me-2 text-danger"></i>
                SAP ERP Authorization/ID Creation Request
              </h3>
              {getSapStatusBadge(reqData.RequestStatus || reqData.Status, false)}
            </div>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.sapAuthorization.list}>SAP Authorization Forms</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Approval (#{reqData.ReferenceNumber || id})
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Link to={routes.sapAuthorization.list} className="btn btn-outline-secondary d-flex align-items-center">
              <i className="ti ti-arrow-left me-1"></i>Back to List
            </Link>
            <button
              type="button"
              className="btn btn-outline-danger d-flex align-items-center px-4"
              onClick={() => handleAction('R')}
              disabled={isProcessing}
            >
              <i className="ti ti-x me-1"></i>Reject
            </button>
            <button
              type="button"
              className="btn btn-success d-flex align-items-center px-4"
              style={{ backgroundColor: '#107C41', borderColor: '#107C41' }}
              onClick={() => handleAction('A')}
              disabled={isProcessing}
            >
              <i className="ti ti-check me-1"></i>Approve
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-2 fs-13">Loading SAP Authorization request details...</p>
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-12">
              {/* Request Status Banner */}
              <div
                className="card border-0 shadow-sm rounded-3 mb-4"
                style={{ backgroundColor: '#FDF3E7', borderLeft: '5px solid #F59E0B' }}
              >
                <div className="card-body p-3 d-flex align-items-center gap-3">
                  <div className="avatar rounded-circle bg-warning-subtle text-warning p-2 d-flex align-items-center justify-content-center">
                    <i className="ti ti-clock-hour-4 fs-24"></i>
                  </div>
                  <div>
                    <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Request Status</span>
                    <h6 className="fw-bold text-warning-emphasis mb-0 fs-15">To Be Approved</h6>
                  </div>
                </div>
              </div>

              {/* Application Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-briefcase me-2 text-danger"></i>Application Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Reference Number</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.ReferenceNumber || id || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Approver ID</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={displayApproverId}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Approver Name</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={displayApproverName}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* User Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-user me-2 text-danger"></i>User Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">User Category</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.NewOrExistingUser === 'E' ? 'Existing SAP User' : 'New SAP User'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Employee No.</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.EmpID || reqData.empId || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Name</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.EmployeeName || reqData.EmpName || reqData.employeeName || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Designation</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.Designation || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Department</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.Department || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Phone Number</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.ContactNumber || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Wing</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.WingText || wingName || reqData.Wing || '-'}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Authorization Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-shield me-2 text-danger"></i>Authorization Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Purchasing Group</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.PurchaseGroup || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Plant</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.Plant || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Business Area</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.BusinessArea || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Purchasing Organization</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.PurchasingOrganization || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">SAP Server</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.SAPServer || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">SAP module</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.SAPModule || '-'}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Codes with Description Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-code me-2 text-danger"></i>Transaction Codes with Description
                  </h5>
                  <span className="badge bg-danger-subtle text-danger px-2 py-1">
                    {tcodesList.length} Items
                  </span>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '80px' }} className="text-center">
                            Sr No
                          </th>
                          <th style={{ width: '220px' }}>Transaction Code</th>
                          <th>Purpose / Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tcodesList.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center py-4 text-muted">
                              No Transaction Codes recorded for this request.
                            </td>
                          </tr>
                        ) : (
                          tcodesList.map((t, idx) => (
                            <tr key={idx}>
                              <td className="text-center fw-bold text-muted">{t.SrNo || idx + 1}</td>
                              <td>
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 fs-13 font-monospace">
                                  {t.TransactionCode}
                                </span>
                              </td>
                              <td className="text-dark fs-13">{t.Remarks || 'Operational Requirement'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Revocation Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-refresh me-2 text-danger"></i>Revocation Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">
                    In case of requesting transactions of already existing user, whether authorizations to be revoked from existing user
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      reqData.AuthorizationRevoke === 'A'
                        ? 'Not Applicable'
                        : reqData.AuthorizationRevoke === 'Y'
                          ? 'Yes'
                          : reqData.AuthorizationRevoke === 'N'
                            ? 'No'
                            : authRevokedName || reqData.AuthorizationRevoke || 'Not Applicable'
                    }
                    readOnly
                    disabled
                  />
                  {reqData.AuthorizationRevoke === 'Y' && (
                    <div className="mt-3">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">
                        Employee number of user whose authorizations are to be revoked
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.ExistingUserID || '-'}
                        readOnly
                        disabled
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Previous Remarks Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-messages me-2 text-danger"></i>Previous Remarks
                  </h5>
                </div>
                <div className="card-body p-4">
                  {remarksTrail.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {remarksTrail.map((r: any, idx: number) => (
                        <div key={idx} className="p-3 bg-light rounded-3 border">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="fw-bold text-dark fs-13">
                              • {r.ApproverName || 'User'} <span className="text-muted fs-11">+{r.ApproverUserID || '-'}</span>
                            </span>
                            <small className="text-muted fs-11">{r.TimeStamp || formatDate(r.created_at || r.CreatedAt)}</small>
                          </div>
                          <div className="p-2 bg-white rounded border text-dark fs-13">
                            {r.HRemarks || r.ARemarks || r.Remarks || '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : reqData.Remarks || reqData.remarks ? (
                    <div className="p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark fs-13">
                          • {reqData.EmployeeName || reqData.EmpName || 'Applicant'} <span className="text-muted fs-11">+{reqData.EmpID || '-'}</span>
                        </span>
                        <small className="text-muted fs-11">{formatDate(reqData.RequestCreationDate || reqData.created_at)}</small>
                      </div>
                      <div className="p-2 bg-white rounded border text-dark fs-13">
                        {reqData.Remarks || reqData.remarks}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-3 text-muted fs-13">
                      No remarks recorded yet.
                    </div>
                  )}
                </div>
              </div>

              {/* Your Remarks Input Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-edit me-2 text-danger"></i>Your Remarks
                  </h5>
                </div>
                <div className="card-body p-4">
                  <label className="form-label fs-13 fw-semibold text-dark mb-2">Any Remarks</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Write Something..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SapAuthorizationApproval;
